import { memo, PropsWithChildren, useEffect } from 'react'
import { useMatomo } from '@jonkoops/matomo-tracker-react'
import { styled } from '@mui/material/styles'
import { useLocation } from 'react-router-dom'

import { Dialog, trackEventFiltered } from '@acre/design-system'

interface GlobalSearchModalProps {
  onClose: () => void
  open: boolean
}

const GlobalSearchModalRoot = styled(Dialog, {
  name: 'AutocompleteField',
  slot: 'Root',
  overridesResolver: (_, styles) => styles.root,
})(({ theme }) => ({
  '> .MuiDialog-container .MuiDialog-paper': {
    position: 'absolute',
    top: '120px',
    maxWidth: theme.wrappers.Large,
    width: '100%',
    background: 'transparent',
    boxShadow: 'none',
    padding: `0 ${theme.spacing(2)}`,
  },
}))

const _GlobalSearchModal = ({ onClose, open, children }: PropsWithChildren<GlobalSearchModalProps>) => {
  const location = useLocation()
  const { trackEvent } = useMatomo()

  // Track when the new search modal is opened, ths is useful to understand how often the new search modal is used versus the old search component
  useEffect(() => {
    if (open) {
      trackEventFiltered(trackEvent, location, 'searchAction', 'openNewSearchModal')
    }
  }, [location, open, trackEvent])

  // Track keyboard events, we want to track whether people use the keyboard to navigate the search modal
  const trackKeyboardEvent = (key: string) => {
    if (key === 'Enter') {
      trackEventFiltered(trackEvent, location, 'searchAction', 'globalSearchEnterKey')
    }
    if (key === 'ArrowUp') {
      trackEventFiltered(trackEvent, location, 'searchAction', 'globalSearchArrowUpKey')
    }
    if (key === 'ArrowDown') {
      trackEventFiltered(trackEvent, location, 'searchAction', 'globalSearchArrowDownKey')
    }
  }

  return (
    <GlobalSearchModalRoot
      open={open}
      onClose={() => {
        // Track when the new search modal is closed without a result click e.g. clicking outside the modal or pressing the escape key
        trackEventFiltered(trackEvent, location, 'searchAction', 'closeNewSearchModal', 'closedWithoutResultClick')
        onClose()
      }}
      disableRestoreFocus
      onKeyDown={(e) => trackKeyboardEvent(e.key)}
    >
      {children}
    </GlobalSearchModalRoot>
  )
}

const GlobalSearchModal = memo(_GlobalSearchModal)

export default GlobalSearchModal
